import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  addSecurityGroupMember,
  createSecurityGroup,
  deleteSecurityGroup,
  getMySecurityGroup,
  getSecurityGroup,
  listSecurityGroupMembers,
  listSecurityGroups,
  removeSecurityGroupMember,
  updateSecurityGroup,
} from '@kirin/api-client/api/security-groups';
import type {
  MySecurityGroup,
  NewSecurityGroup,
  SecurityGroup,
  UpdateSecurityGroup,
} from '@kirin/api-client/contracts/security-groups';
import { ApiError } from '@kirin/api-client/errors';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * List an organization's security groups (KIR-3800).
 *
 * The list response carries `userCount` per group, which the Policies scope
 * selector and the groups table both render. Single-group responses omit it.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the query (default: true when orgId provided)
 * @returns React Query result with the security group array
 */
export function useSecurityGroups(
  organizationId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.securityGroups.list(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listSecurityGroups(apiClient, organizationId);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Every security group of an organization, deleted ones included, each with
 * `deletedAt` (KIR-4701). For views that keep naming a group after it is
 * deleted, such as Audit Logs; selectors use {@link useSecurityGroups}.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the query (default: true when orgId provided)
 * @returns React Query result with the security group array
 */
export function useSecurityGroupsIncludingDeleted(
  organizationId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.securityGroups.listIncludingDeleted(
      organizationId ?? ''
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listSecurityGroups(apiClient, organizationId, {
        includeDeleted: true,
      });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * The security group the CURRENT user belongs to (KIR-4334), or `{ group: null }`.
 *
 * The member-facing counterpart to {@link useSecurityGroups}, whose endpoint is
 * manager/owner/support — so a member cannot use it to find their own group.
 *
 * Only **404** resolves to `{ group: null }`. That is the flag gate's own answer
 * (the whole security-groups surface 404s when `security_groups_policies` is off,
 * deliberately "not found" rather than "forbidden"), and the org scope is the
 * correct view for an org without the feature.
 *
 * Every other failure — 403, 5xx, network — is left to reject. It used to resolve
 * to `{ group: null }` too, which quietly re-created the bug KIR-4334 exists to
 * fix: the caller reads "no group", renders the ORGANIZATION policies, and the
 * member is shown the default policy as though it were the one assigned to them.
 * A 403 here is not "no group", it is a broken gate on a route that is `anyRole`
 * by design. Callers must handle `isError` by refusing to render a policy list.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the query (default: true when orgId provided)
 * @returns React Query result with `{ group }`
 */
export function useMySecurityGroup(
  organizationId: string | undefined,
  enabled = true
) {
  return useQuery<MySecurityGroup>({
    queryKey: queryKeys.securityGroups.mine(organizationId ?? ''),
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      try {
        return await getMySecurityGroup(apiClient, organizationId);
      } catch (error) {
        // 404 only — the flag gate. Anything else must surface; see the note above.
        if (error instanceof ApiError && error.status === 404) {
          return { group: null };
        }
        throw error;
      }
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    // The flag-off 404 is handled above, so a rejection here is a real fault the
    // caller has to show. Retrying only delays that; the error UI offers a retry.
    //
    // `networkMode: 'always'` — the default `'online'` pauses the fetch while
    // the browser is offline. A pause is `isPending` with `isError` false and
    // `isLoading` false, which PoliciesTab reads as "this member has no group"
    // and renders the organization policies (KIR-4334). Failing the request
    // surfaces `isError` instead.
    retry: false,
    networkMode: 'always',
  });
}

/**
 * Fetch a single security group by id.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @param enabled - Whether to run the query (default: true when both provided)
 * @returns React Query result with the security group
 */
export function useSecurityGroup(
  organizationId: string | undefined,
  groupId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.securityGroups.detail(
      organizationId ?? '',
      groupId ?? ''
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!groupId) throw new Error('groupId is required');
      return getSecurityGroup(apiClient, organizationId, groupId);
    },
    enabled: enabled && !!organizationId && !!groupId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * List a security group's members. Returned `userId`s are Descope IDs, matching
 * the ids the organization members list already uses.
 *
 * @param organizationId - The organization ID
 * @param groupId - The security group ID
 * @param enabled - Whether to run the query (default: true when both provided)
 * @returns React Query result with the membership rows
 */
export function useSecurityGroupMembers(
  organizationId: string | undefined,
  groupId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.securityGroups.members(
      organizationId ?? '',
      groupId ?? ''
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!groupId) throw new Error('groupId is required');
      return listSecurityGroupMembers(apiClient, organizationId, groupId);
    },
    enabled: enabled && !!organizationId && !!groupId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Map every grouped user to the group they belong to (KIR-3800, AC #11).
 *
 * The organization members API carries no group field, so the mapping is
 * derived client-side: list the groups, then read each group's members. That is
 * 1 + N requests, which is acceptable because N is the number of *groups* (a
 * small, admin-curated set), not the number of users. If groups ever grow
 * unbounded, the right fix is a `securityGroup` field on the members response
 * rather than more fan-out here.
 *
 * A user belongs to at most one group, so the resulting map is unambiguous.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the queries (default: true when orgId given)
 * @returns `groupByUserId` (Descope user ID → group) and a combined `isLoading`
 */
export function useSecurityGroupByUser(
  organizationId: string | undefined,
  enabled = true
): {
  groupByUserId: Record<string, SecurityGroup>;
  /** Every group in the org, including ones with no members. */
  groups: SecurityGroup[];
  isLoading: boolean;
} {
  const { data: groups, isLoading: isLoadingGroups } = useSecurityGroups(
    organizationId,
    enabled
  );

  const groupList = useMemo(() => groups ?? [], [groups]);

  /*
    `combine` rather than a `useMemo` over the query results.

    `useQueries` returns a FRESH array of fresh result objects on every render,
    so memoizing the derived map on those objects never actually memoizes — it
    recomputed each render and, worse, handed downstream consumers
    (MembersTab's `securityGroupNameByUserId`) a new object identity every time,
    defeating their memoization too. `combine` is structurally memoized by
    TanStack: the returned value keeps its identity until the underlying query
    data actually changes.
  */
  const { groupByUserId, isLoadingMembers } = useQueries({
    queries: groupList.map(group => ({
      queryKey: queryKeys.securityGroups.members(
        organizationId ?? '',
        group.id
      ),
      queryFn: () => {
        if (!organizationId) throw new Error('organizationId is required');
        return listSecurityGroupMembers(apiClient, organizationId, group.id);
      },
      enabled: enabled && !!organizationId,
      ...cacheConfigForCollaborativeData,
      retry: retryHandlers.list,
    })),
    combine: results => {
      const map: Record<string, SecurityGroup> = {};
      groupList.forEach((group, index) => {
        results[index]?.data?.forEach(member => {
          map[member.userId] = group;
        });
      });
      return {
        groupByUserId: map,
        isLoadingMembers: results.some(result => result.isLoading),
      };
    },
  });

  return {
    groupByUserId,
    groups: groupList,
    isLoading: isLoadingGroups || isLoadingMembers,
  };
}

/**
 * Create a security group.
 *
 * @param organizationId - The organization ID
 * @returns React Query mutation taking `NewSecurityGroup`
 */
export function useCreateSecurityGroup(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NewSecurityGroup) => {
      if (!organizationId) throw new Error('organizationId is required');
      return createSecurityGroup(apiClient, organizationId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.securityGroups.all,
      });
    },
  });
}

/**
 * Rename a security group or edit its description.
 *
 * @param organizationId - The organization ID
 * @returns React Query mutation taking `{ groupId, data }`
 */
export function useUpdateSecurityGroup(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: string;
      data: UpdateSecurityGroup;
    }) => {
      if (!organizationId) throw new Error('organizationId is required');
      return updateSecurityGroup(apiClient, organizationId, groupId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.securityGroups.all,
      });
    },
  });
}

/**
 * Delete a security group. Members are not deleted — they fall back to the
 * organization's policies, so the group-scoped policy caches must go too.
 *
 * @param organizationId - The organization ID
 * @returns React Query mutation taking the group ID
 */
export function useDeleteSecurityGroup(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => {
      if (!organizationId) throw new Error('organizationId is required');
      return deleteSecurityGroup(apiClient, organizationId, groupId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.securityGroups.all,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
    },
  });
}

/**
 * Add a user to a security group.
 *
 * A user belongs to at most one group per organization, so the backend *moves*
 * a user who is already in another group. That makes every group's member list
 * and every group's `userCount` potentially stale — invalidate the whole
 * security-groups tree rather than just the target group.
 *
 * @param organizationId - The organization ID
 * @returns React Query mutation taking `{ groupId, userId }`
 */
export function useAddSecurityGroupMember(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) => {
      if (!organizationId) throw new Error('organizationId is required');
      return addSecurityGroupMember(apiClient, organizationId, groupId, {
        userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.securityGroups.all,
      });
    },
  });
}

/**
 * Remove a user from a security group. The user returns to the organization's
 * policies.
 *
 * @param organizationId - The organization ID
 * @returns React Query mutation taking `{ groupId, userId }`
 */
export function useRemoveSecurityGroupMember(
  organizationId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) => {
      if (!organizationId) throw new Error('organizationId is required');
      return removeSecurityGroupMember(
        apiClient,
        organizationId,
        groupId,
        userId
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.securityGroups.all,
      });
    },
  });
}
